import $apiJSON from '../http/apiJSON'

// Community API Service

// Posts API
export async function getCommunityPosts(params = {}) {
  return $apiJSON.get('community/posts/', { params })
}

export async function getCommunityPost(slug) {
  return $apiJSON.get(`community/posts/${slug}/`)
}

export async function createCommunityPost(data) {
  return $apiJSON.post('community/posts/', data)
}

export async function updateCommunityPost(slug, data) {
  return $apiJSON.put(`community/posts/${slug}/`, data)
}

export async function deleteCommunityPost(slug) {
  return $apiJSON.delete(`community/posts/${slug}/`)
}

export async function likeCommunityPost(slug) {
  return $apiJSON.post(`community/posts/${slug}/like/`)
}

export async function getPostComments(slug) {
  return $apiJSON.get(`community/posts/${slug}/comments/`)
}

export async function addPostComment(slug, content) {
  return $apiJSON.post(`community/posts/${slug}/add-comment/`, { content })
}

export async function trackPostView(slug) {
  return $apiJSON.post(`community/posts/${slug}/view/`)
}

export async function getFeaturedPosts() {
  return $apiJSON.get('community/posts/featured/')
}

export async function getTrendingPosts() {
  return $apiJSON.get('community/posts/trending/')
}

// Categories API
export async function getCommunityCategories() {
  return $apiJSON.get('community/categories/')
}

// Users API  
export async function getCommunityUsers(params = {}) {
  return $apiJSON.get('community/users/', { params })
}

export async function followUser(userId) {
  return $apiJSON.post(`community/users/${userId}/follow/`)
}

export async function unfollowUser(userId) {
  return $apiJSON.post(`community/users/${userId}/unfollow/`)
}

export async function getUserProfile(userId) {
  return $apiJSON.get(`community/users/${userId}/profile/`)
}

export async function getUserPosts(userId, params = {}) {
  return $apiJSON.get(`community/users/${userId}/posts/`, { params })
}

export async function getUserFollowers(userId) {
  return $apiJSON.get(`community/users/${userId}/followers/`)
}

export async function getUserFollowing(userId) {
  return $apiJSON.get(`community/users/${userId}/following/`)
}

// Feed and Search API
export async function getCommunityFeed() {
  return $apiJSON.get('community/feed/')
}

export async function searchCommunity(query) {
  return $apiJSON.get('community/search/', { params: { q: query } })
}

export async function getCommunityStats() {
  return $apiJSON.get('community/stats/')
}

// Chat Rooms API
export async function getChatRooms(params = {}) {
  return $apiJSON.get('community/chat-rooms/', { params })
}

export async function getChatRoom(id) {
  return $apiJSON.get(`community/chat-rooms/${id}/`)
}

export async function joinChatRoom(id) {
  return $apiJSON.post(`community/chat-rooms/${id}/join/`)
}

export async function leaveChatRoom(id) {
  return $apiJSON.post(`community/chat-rooms/${id}/leave/`)
}

export async function getChatMessages(roomId, params = {}) {
  return $apiJSON.get(`community/chat-rooms/${roomId}/messages/`, { params })
}

export async function sendChatMessage(data) {
  return $apiJSON.post('community/chat-messages/', data)
}

// Forum API
export async function getForums(params = {}) {
  return $apiJSON.get('community/forums/', { params })
}

export async function createForum(data) {
  return $apiJSON.post('community/forums/', data)
}

export async function getForumThreads(forumId, params = {}) {
  return $apiJSON.get(`community/forums/${forumId}/threads/`, { params })
}

export async function createForumThread(forumId, data) {
  return $apiJSON.post('community/forum-threads/', {
    ...data,
    forum: forumId
  })
}

export async function getForumPosts(threadId, params = {}) {
  return $apiJSON.get(`community/forum-threads/${threadId}/posts/`, { params })
}

export async function createForumPost(threadId, data) {
  return $apiJSON.post('community/forum-posts/', {
    ...data,
    thread: threadId
  })
}

export async function getForum(forumId) {
  return $apiJSON.get(`community/forums/${forumId}/`)
}

export async function getForumThread(threadId) {
  return $apiJSON.get(`community/forum-threads/${threadId}/`)
}

export async function joinByInviteCode(invite_code) {
  return $apiJSON.post('community/chat-rooms/join-by-invite/', { invite_code })
}

export async function kickChatMember(roomSlug, userId) {
  return $apiJSON.post(`community/chat-rooms/${roomSlug}/kick/`, { user_id: userId })
}

export async function regenerateInviteCode(roomSlug) {
  return $apiJSON.post(`community/chat-rooms/${roomSlug}/regenerate-invite/`)
}

export async function getChatMembers(roomSlug) {
  return $apiJSON.get(`community/chat-rooms/${roomSlug}/members/`)
}

export async function updateChatRoom(roomSlug, data) {
  return $apiJSON.patch(`community/chat-rooms/${roomSlug}/`, data)
}

export async function deleteChatRoom(roomSlug) {
  return $apiJSON.delete(`community/chat-rooms/${roomSlug}/`)
}

export async function createChatRoom(data) {
  return $apiJSON.post('community/chat-rooms/', data)
}

export async function markChatRead(slug) {
  return $apiJSON.post(`community/chat-rooms/${slug}/mark-read/`)
}

export async function getTotalUnread() {
  return $apiJSON.get('community/chat-rooms/total-unread/')
}

// Class wrapper for convenience
class CommunityService {
  // Posts
  static getCommunityPosts = getCommunityPosts
  static getCommunityPost = getCommunityPost
  static createCommunityPost = createCommunityPost
  static updateCommunityPost = updateCommunityPost
  static deleteCommunityPost = deleteCommunityPost
  static likeCommunityPost = likeCommunityPost
  static getPostComments = getPostComments
  static addPostComment = addPostComment
  static getFeaturedPosts = getFeaturedPosts
  static getTrendingPosts = getTrendingPosts
  
  // Categories & Search
  static getCommunityCategories = getCommunityCategories
  static getCommunityStats = getCommunityStats
  static searchCommunity = searchCommunity
  
  // Users
  static getCommunityUsers = getCommunityUsers
  static getUserProfile = getUserProfile
  static getUserPosts = getUserPosts
  
  // Chat
  static getChatRooms = getChatRooms
  static getChatRoom = getChatRoom
  static joinChatRoom = joinChatRoom
  static leaveChatRoom = leaveChatRoom
  static getChatMessages = getChatMessages
  static sendChatMessage = sendChatMessage
  static joinByInviteCode = joinByInviteCode
  static kickChatMember = kickChatMember
  static regenerateInviteCode = regenerateInviteCode
  static getChatMembers = getChatMembers
  static updateChatRoom = updateChatRoom
  static deleteChatRoom = deleteChatRoom
  static markChatRead = markChatRead
  static getTotalUnread = getTotalUnread

  // Forum
  static getForums = getForums
  static createForum = createForum
  static getForumThreads = getForumThreads
  static createForumThread = createForumThread
  static getForumPosts = getForumPosts
  static createForumPost = createForumPost
  static getForum = getForum
  static getForumThread = getForumThread
  static createChatRoom = createChatRoom
  
  // Legacy methods
  static getPosts = getCommunityPosts
  static getCategories = getCommunityCategories
}

export default CommunityService